import { useMemo } from 'react';
import { XIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { cleanTagsFromText, decodeEntities } from '~/utils';
import type { TextOrFileMessage } from '../../types';

/**
 * A preview of the message being replied to or forwarded, shown above the chat input.
 * @see {@link ChatBoxInput}
 */
export function ChatBoxReplyPreview({
    className,
    message,
    mode = 'reply',
    onCancel,
}: {
    className?: string;
    message: TextOrFileMessage;
    /**
     * The mode of the preview. Defaults to `'reply'`.
     * - `'reply'`: Shows "Reply to {sender name}"
     * - `'forward'`: Shows "Forward message from {sender name}"
     */
    mode?: 'reply' | 'forward';
    onCancel?: () => void;
}) {
    const senderName = useMemo(() => {
        const sender = message.Participant.Identity;
        if (sender.__typename === 'Professional') {
            return message.CreatedBy
                ? `${message.CreatedBy} (${sender.Title})`
                : sender.Title;
        } else if (message.CreatedBy) {
            return message.CreatedBy;
        }
        return sender.Name;
    }, [message]);
    const previewText = useMemo(() => {
        if (message.Content) {
            return decodeEntities(cleanTagsFromText(message.Content));
        }
        // File attachment without text content.
        if (
            message.__typename === 'FileAttachmentMessage' &&
            message.Attachments?.length
        ) {
            const fileCount = message.Attachments.length;
            return fileCount === 1
                ? (message.Attachments[0]!.FileName ?? 'Attachment')
                : `${fileCount} attachments`;
        }
        return '';
    }, [message]);
    const labelText = useMemo(() => {
        return mode === 'forward'
            ? `Forward message from ${senderName}`
            : `Reply to ${senderName}`;
    }, [mode, senderName]);
    const ariaLabel = useMemo(() => {
        return mode === 'forward' ? 'Cancel forward' : 'Cancel reply';
    }, [mode]);

    return (
        <div
            className={cn(
                'flex items-start gap-[8px] rounded-[8px] bg-gray-100 p-[8px]',
                className
            )}
        >
            <div className="min-w-0 flex-1 text-[12px]">
                <div className="font-medium">{labelText}</div>
                <div className="truncate text-black/70">{previewText}</div>
            </div>
            <button
                className="flex size-[20px] shrink-0 items-center justify-center rounded-full hover:bg-black/10"
                aria-label={ariaLabel}
                onClick={onCancel}
            >
                <XIcon className="size-[14px]" />
            </button>
        </div>
    );
}
